import { motion } from "framer-motion";
import { ArrowUpRight, BarChart3, Code2, Compass, LifeBuoy } from "lucide-react";
import { useLanguage } from "@/i18n";
import { BRAND, scrollToSection } from "@/constants/brand";

const ICONS = { software: Code2, consulting: Compass, management: BarChart3, support: LifeBuoy };

const reveal = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
};

const BookAction = ({ label, onClick }) => (
  <button
    data-testid="service-card-book-action"
    onClick={onClick}
    className="group/btn mt-6 inline-flex items-center gap-2 font-tech text-xs uppercase tracking-[0.18em] text-emerald-400 transition-colors hover:text-emerald-300"
  >
    {label}
    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
  </button>
);

export default function Services({ onSelect }) {
  const { t } = useLanguage();
  const cards = t("services.cards");
  const [software, consulting, management, support] = cards;

  const pick = (id) => {
    onSelect(id);
    scrollToSection("agendar");
  };

  return (
    <section id="servicos" data-testid="services-section" className="relative py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div {...reveal} transition={{ duration: 0.7 }} className="mb-14 max-w-2xl">
          <span className="eyebrow">{t("services.eyebrow")}</span>
          <h2 className="font-display mt-4 text-3xl font-normal tracking-tight text-slate-50 sm:text-4xl">
            {t("services.title")}
          </h2>
          <p className="mt-4 text-base leading-relaxed text-slate-400">{t("services.sub")}</p>
        </motion.div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-12">
          <motion.article
            {...reveal}
            transition={{ duration: 0.7, delay: 0.1 }}
            data-testid="service-card-software-dev"
            className="group relative overflow-hidden rounded-2xl border border-emerald-500/20 bg-gradient-to-b from-emerald-950/30 to-black/60 p-8 md:col-span-7 md:row-span-2"
          >
            <img
              src={BRAND.images.security}
              alt=""
              className="absolute inset-0 h-full w-full object-cover opacity-15 transition-opacity duration-500 group-hover:opacity-25"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#030706] via-[#030706]/60 to-transparent" />
            <div className="relative flex h-full flex-col justify-end">
              <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl border border-emerald-500/30 bg-emerald-500/10">
                <Code2 className="h-6 w-6 text-emerald-400" />
              </div>
              <h3 className="font-display text-2xl text-slate-50 sm:text-3xl">{software.title}</h3>
              <p className="mt-3 max-w-md text-sm leading-relaxed text-slate-300 sm:text-base">{software.desc}</p>
              <div className="mt-5 flex flex-wrap gap-2">
                {software.points.map((p) => (
                  <span key={p} className="rounded-full border border-emerald-500/25 bg-emerald-500/10 px-3 py-1 font-tech text-[11px] text-emerald-300">{p}</span>
                ))}
              </div>
              <BookAction label={t("services.cta")} onClick={() => pick(software.id)} />
            </div>
          </motion.article>

          {[consulting, management].map((card, i) => {
            const Icon = ICONS[card.id];
            return (
              <motion.article
                key={card.id}
                {...reveal}
                transition={{ duration: 0.7, delay: 0.15 + i * 0.08 }}
                data-testid={card.id === "consulting" ? "service-card-it-consulting" : "service-card-business-mgmt"}
                className="group rounded-2xl border border-emerald-500/15 bg-black/40 p-8 transition-colors duration-300 hover:border-emerald-500/35 md:col-span-5"
              >
                <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl border border-emerald-500/30 bg-emerald-500/10">
                  <Icon className="h-6 w-6 text-emerald-400" />
                </div>
                <h3 className="font-display text-xl text-slate-50 sm:text-2xl">{card.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-400">{card.desc}</p>
                <BookAction label={t("services.cta")} onClick={() => pick(card.id)} />
              </motion.article>
            );
          })}

          <motion.article
            {...reveal}
            transition={{ duration: 0.7, delay: 0.2 }}
            data-testid="service-card-tech-support"
            className="group relative overflow-hidden rounded-2xl border border-emerald-500/20 bg-[#06120d]/60 md:col-span-12"
          >
            <div className="flex flex-col items-stretch md:flex-row">
              <div className="flex flex-1 flex-col justify-center p-8">
                <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl border border-emerald-500/30 bg-emerald-500/10">
                  <LifeBuoy className="h-6 w-6 text-emerald-400" />
                </div>
                <h3 className="font-display text-xl text-slate-50 sm:text-2xl">{support.title}</h3>
                <p className="mt-3 max-w-xl text-sm leading-relaxed text-slate-400">{support.desc}</p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {support.points.map((p) => (
                    <span key={p} className="rounded-full border border-emerald-500/25 bg-emerald-500/10 px-3 py-1 font-tech text-[11px] text-emerald-300">{p}</span>
                  ))}
                </div>
                <BookAction label={t("services.cta")} onClick={() => pick(support.id)} />
              </div>
              <div className="relative min-h-[180px] md:w-2/5">
                <img src={BRAND.images.infrastructure} alt="" className="absolute inset-0 h-full w-full object-cover opacity-50" />
                <div className="absolute inset-0 bg-gradient-to-r from-[#06120d] to-transparent" />
              </div>
            </div>
          </motion.article>
        </div>
      </div>
    </section>
  );
}
